import {
  ButtonGroup,
  type ButtonProps,
  type StackProps,
  Heading,
  Image,
  Text,
  VStack,
  Center,
  Stack
} from '@chakra-ui/react'
import React from 'react'
import type { ElementType, PropsWithChildren } from 'react'
import { SystemStyleObject } from '@chakra-ui/styled-system'

export type ErrorContentProps =
  & StackProps
  & PropsWithChildren<{
  headingText: string
  messageText?: string | null
  imageSrc?: string | null
  buttons?: Array<ElementType<ButtonProps>> | null
  layout?: 'vertical' | 'horizontal'
  contentBoxType?: string | null
}>

/**
 * Figma design
 * https://www.figma.com/file/af9kkSa6gGOPbmy3xG1geE/Student-Centre-for-DEV?type=design&node-id=5608-301975&mode=design&t=FDsyWd9nQHmocsNy-0
 */
export function ErrorContent({
  headingText,
  imageSrc = null,
  messageText = null,
  buttons = null,
  children = null,
  layout = 'horizontal',
  contentBoxType = null,
  ...restProps
}: ErrorContentProps) {
  const isVertical = layout === 'vertical'
  const contentBg: SystemStyleObject = getContentBg(contentBoxType)

  return (
    <Stack
      direction={isVertical ? 'column' : {
        base: 'column',
        modalSmall: 'row'
      }}
      alignItems={isVertical ? 'center' : {
        base: 'center',
        modalContentMedium: 'flex-start'
      }}
      spacing={0}
      w={'100%'}
      {...restProps}
    >
      {imageSrc &&
        <Image
          src={imageSrc}
          alt={'Error'}
          marginLeft={isVertical ? 0 : {
            base: 0,
            modalSmall: '-40px'
          }}
          width={{
            base: isVertical ? '180px' : '357px',
            modalContentMedium: isVertical ? '220px' : '410px'
          }}
          margin={'5px'}
        />
      }
      <Center
        sx={contentBg}
        maxWidth={contentBoxType ? '518px': 'unset'}
        minHeight={contentBoxType ? '392' : 'unset'}
        borderRadius={'0px 24px 24px 24px'}
        margin={'5px !important'}
      >
        <VStack
          w={'calc(100% - 88px)'}
          minH={'calc(100% - 88px)'}
          align={'start'}
          spacing={'24px'}
          m={'44px'}
        >
          <Heading
            w={'100%'}
            fontWeight={'400'}
            fontSize={isVertical ? '30px' : {
              base: '30px',
              modalContentMedium: '44px'
            }}
            lineHeight={'130%'}
            letterSpacing={'-1.2px'}
            color={'neutral.900'}
            size={'md'}
            dangerouslySetInnerHTML={{
              __html: headingText
            }}
            textAlign={isVertical ? 'center' : 'left'}
          />
          {messageText && <Text
            w={'100%'}
            fontWeight={'400'}
            fontSize={'20px'}
            lineHeight={'140%'}
            letterSpacing={'-0.2px'}
            color={'neutral.600'}
            dangerouslySetInnerHTML={{
              __html: messageText
            }}
            textAlign={isVertical ? 'center' : 'left'}
          />}
          {children}
          {Array.isArray(buttons) &&
            <ButtonGroup
              w={'100%'}
              justifyContent={isVertical ? 'center' : {
                base: 'center',
                modalContentMedium: 'flex-start'
              }}
            >
              {buttons.map((Button, index) => (
                <Button
                  key={index}
                />
              ))
              }
            </ButtonGroup>
          }
        </VStack>
      </Center>
    </Stack>
  )
}

function getContentBg(contentBoxType: string | null) {
  switch (contentBoxType) {
  case 'white':
    return { background: 'white' }
  case 'faded':
    return { background: 'rgba(255, 255, 255, 0.24)' }
  case null:
    return { background: 'none' }
  default:
    return { background: contentBoxType }
  }
}
